// Shared valuation logic — the single source of truth for BOXD market values.
// Imported by src/App.js (browser, via webpack) and scripts/ingest-box-office.mjs
// (Node). CommonJS so both consumers can load it without a build step.
//
// If you change the formula here, that's the only place it needs to change.

const { TINY_FLOOR_EST, postOpeningMultiple, legsBandOf } = require('./scoring.js')

// IPO / base price from a pre-release opening-weekend estimate ($M).
// Floor lifted ($3 -> $4) and top compressed ($130 -> $105 ceiling) so the
// whole 6-film roster costs real budget regardless of which films you pick,
// and the cheap end has actual spread instead of a $3M blob.
//
// Any curve that both inflates cheap films and compresses expensive ones has
// to cross IPO===est somewhere (continuity forces it). That crossing used to
// land at ~$22M est — right in the densest part of the real slate (most
// ordinary wide releases open in the $15-35M range) — so a huge share of
// films priced at essentially their raw estimate. The curve below is
// retuned so the crossing sits at $35M instead, clear of that dense band,
// while keeping the exact same $105-cap crossing point (~$178M est) as
// before so blockbuster differentiation ($115M vs $175M vs $280M) is
// unchanged: ~$6 at est $1M, ~$11 at $5, ~$20 at $15, ~$35 at $35
// (crossover), ~$65 at $88, ~$104 at $175.
//
// $35M was picked over other crossing points by checking real slate-
// diversity impact against the live film pool, not just price level: it
// clears more distinct budget-legal 6-film combinations than today's curve
// in every phase (roughly +100% in Phase 1, +6% in Phase 2, +19% in
// Phase 3), while every phase still keeps >= $15M of headroom on an
// average roster — Phase 3's smaller, pricier pool is the tightest and
// sets that ceiling.
//
// Floor lowered again from $7 (constant 6) to $4 (constant 3): the old
// floor made the cheapest films (under ~$1M est) carry a 8-35x markup over
// their own estimate, far steeper than anywhere else on the curve, while
// doing no extra work -- the mandatory 80%-of-budget deploy rule already
// forces spend into pricier films regardless of where the floor sits.
// Re-solved for the SAME $35M crossover and SAME $178M cap point, so
// nothing above ~$10M est moves by more than $1 -- this only softens the
// sub-$10M tail.
function calcIPOprice (est) {
  if (est == null || isNaN(est)) return null
  if (est <= 0) return 4
  return Math.max(4, Math.min(105, Math.round(3 + 2.5387516078192225 * Math.pow(est, 0.7127469355319692))))
}

// How far a film's real result sits from its own estimate, for PRICING
// purposes. Same $5M tiny-film floor concept as the points side (a film
// estimated under $5M can't be treated as worse than a 70% result when
// pricing it), but price needs the real, continuous ratio rather than a
// bucket — a coarse bucket would flatten a 206%-of-estimate result and a
// 407%-of-estimate result into the same price move, which throws away
// exactly the information a trade value needs to reflect a genuinely
// historic result.
function priceRatio (estM, actualM) {
  const raw = actualM / estM
  if (estM < TINY_FLOOR_EST) return Math.max(raw, 0.70)
  return raw
}

// One continuous curve across the whole range, not a patchwork of bands.
// Meeting the estimate exactly still means no change (perf = 1). Beating it
// compounds a bit faster than 1-for-1 the further past it you go, so a
// genuine double is worth meaningfully more than a 20% beat, and a
// quadrupling is worth more again, rather than every big beat landing on
// the same ceiling. Missing it falls away a bit faster than 1-for-1 too.
// Capped at 4x, the system's overall trade-value ceiling (also enforced
// below, so this cap is belt-and-braces).
function pricePerf (r) { return Math.min(4, Math.pow(r, 1.3)) }

// Critics multiplier — small and separate from the opening-weekend price
// move above, six gentle Rotten Tomatoes bands.
function priceRt (rt) {
  if (rt == null) return 1
  return rt >= 90 ? 1.08 : rt >= 80 ? 1.05 : rt >= 70 ? 1.03 : rt >= 60 ? 1.00 : rt >= 50 ? 0.97 : 0.95
}

// Market value after results land.
//   film: { basePrice, estM, rt }
//   actualM: opening weekend gross ($M), or null (returns basePrice)
//   weeklyGrosses: { 2: $M, 3: $M, ... 6: $M } for the legs bonus
function calcMarketValue (film, actualM, weeklyGrosses = {}) {
  if (actualM == null) return film.basePrice
  const r = film.estM ? priceRatio(film.estM, actualM) : 1
  const perf = pricePerf(r)
  const rt = priceRt(film.rt)
  // Same multiple-based legs as the points side, at a smaller scale: a true
  // word-of-mouth phenomenon (3x+ its opening weekend) adds up to +30% on
  // top of whatever the opening result already earned.
  const legsMult = 1 + legsBandOf(postOpeningMultiple(actualM, weeklyGrosses)).priceBump
  const raw = film.basePrice * perf * rt * legsMult
  return Math.round(Math.max(film.basePrice * 0.15, Math.min(film.basePrice * 4, raw)))
}

module.exports = { calcIPOprice, calcMarketValue, priceRatio, pricePerf, priceRt }
